import DOMPurify from "dompurify";
import { getContext, setContext } from "svelte";
import { writable, type Writable } from "svelte/store";
import type { ToastItem } from "./types";

const CONTEXT_KEY = {};

type StoreProps = {
	items: ToastItem[];
};

export type ToastOptions = Partial<Omit<ToastItem, "message">>;
export type InfoToastOptions = Omit<ToastOptions, "type" | "cssClasses">;

type ToastStore = Writable<StoreProps> & {
	/**
	 * Pushes a generic toast object to the queue
	 * @param {string} message HTML formatted string that will be displayed to the user
	 * @param {Object} [options] Options related to the toast
	 * @param {string} [options.id] toast unique identifier
	 * @param {ToastItemType}	[options.type] ToastItemType
	 * @param {(itemId: string) => void} [options.onClose] Callback function that gets the toast id once the toast is closed
	 * @param {number} [options.duration] Number of milliseconds in which the toast will be closed
	 * @param {boolean}	[options.canClose] If `true`, the toast will be displayed with an X sign to allow the user to manually close it
	 * @param {boolean}	[options.autoClose] If `true`, the toast will be automatically closed after `duration` amount of milliseconds
	 * @param {boolean}	[options.showProgress] If `true`, a progress of when the toast will be automatically closed will be shown
	 * @param {Array<string>} [options.cssClasses] A list of CSS class names to attach to the toast
	 * @returns {void}
	 */
	push(message: string, options?: ToastOptions): void;

	/**
	 * Pushes an Info styled (green background and more...) message to the queue
	 * @param messageContent HTML formatted string that will be displayed to the user
	 * @param {Object} [options] Options related to the toast
	 * @param {string} [options.id] toast unique identifier
	 * @param {(itemId: string) => void} [options.onClose] Callback function that gets the toast id once the toast is closed
	 * @param {number} [options.duration] Number of milliseconds in which the toast will be closed
	 * @param {boolean}	[options.canClose] If `true`, the toast will be displayed with an X sign to allow the user to manually close it
	 * @param {boolean}	[options.autoClose] If `true`, the toast will be automatically closed after `duration` amount of milliseconds
	 * @param {boolean}	[options.showProgress] If `true`, a progress of when the toast will be automatically closed will be shown
	 * @returns {void} `void`
	 */
	pushInfo(messageContent: string, options?: InfoToastOptions): void;
	/**
	 * Removes a toast object from the queue
	 * @param {(string)} id Toast ID to remove
	 * @returns {void}
	 */
	remove(id: string): void;
};

export function getToastStore() {
	const store = getContext(CONTEXT_KEY) as ToastStore;
	if (!store) {
		throw new Error("you need to call createToastStore first");
	}
	return store;
}

export function createToastStore(): ToastStore {
	const store = createStore();
	setContext(CONTEXT_KEY, store);
	return store;
}

function createStore(): ToastStore {
	const store = writable<StoreProps>({
		items: []
	});

	return {
		subscribe: store.subscribe,
		update: store.update,
		set: store.set,
		push(message: string, options: ToastOptions = {}) {
			store.update((values) => {
				if (values.items.find((item) => item.id === options?.id)) {
					return values;
				}
				const newItem: ToastItem = {
					id: options.id || Math.random().toString(36).substr(2, 9),
					message,
					type: options.type || "info",
					duration: options.duration === undefined ? 4000 : options.duration,
					onClose: options.onClose,
					canClose: options.canClose === undefined ? true : options.canClose,
					autoClose: options.autoClose === undefined ? false : options.autoClose,
					showProgress: options.showProgress === undefined ? false : options.showProgress,
					cssClasses: options.cssClasses === undefined ? ["bg-white", "text-gray-600"] : options.cssClasses
				};
				return {
					...values,
					items: [newItem, ...values.items]
				};
			});
		},
		pushInfo(messageContent: string, options: InfoToastOptions = {}) {
			const sanitizedContent = DOMPurify.sanitize(messageContent);

			this.push(`<p class='flex items-center shrink-0 text-sm'>${sanitizedContent}</p>`, {
				...options,
				cssClasses: [
					"bg-[#1C8313] text-white [&_.border-message]:border-[#11992F]",
					"[&_a]:border [&_a]:rounded-lg [&_a]:px-4 [&_a]:py-2 [&_a]:border-gray-200"
				]
			});
			{
			}
		},
		remove(id) {
			store.update((values) => {
				return {
					...values,
					items: values.items.filter((item) => item.id !== id)
				};
			});
		}
	};
}
